<template>
    <div class="container">
        <div class="row center">
            <div class="col-lg-7">
                <label>{{ $t('label.from') }}</label>
                <datepicker format="DD MMMM YYYY"
                            class="with-icon"
                            v-model="range"
                ></datepicker>
            </div>
        </div>
        <div class="row around">
            <div class="col-lg-5 col-md-12">
                <h3>
                    {{ $t('label.topWorkers') }}: <br /> <small>({{ range[0] | formatDate }}-{{ range[1] | formatDate }})</small>
                </h3>
                <div class="select">
                    <select name="filter" v-model="filter">
                        <option value="profit" selected="selected">{{ $t('label.perProfit') }}</option>
                        <option value="customer">{{ $t('label.perCustomers') }}</option>
                    </select>
                </div>
                <ol class="stats-list">
                    <li v-for="(employee, index) in employeeStats">
                        <user-image :image="employee.image" :color="employee.color"></user-image>
                        <div class="stats-list-line">
                            <span>{{ employee.name }}</span>
                            <span>{{ filter === 'profit' ? profit(employee) : customers(employee) }}</span>
                        </div>
                    </li>
                </ol>
            </div>
            <div class="col-lg-5 col-md-12">
                <div v-if="visible">
                    <h3>
                        {{ $t('label.services') }}
                    </h3>
                    <doughnut :chartData="doughnutChartData" cssClasses="stats-doughnut-chart"></doughnut>
                </div>
            </div>
        </div>
        <div v-if="visible">
            <div class="row center">
                <div class="col-lg-11 col-md-12">
                    <hr class="extra" />
                </div>
            </div>
            <div class="row around">
                <div class="col-lg-5 col-md-12">
                    <h3>
                        {{ $t('label.originalProfit') }}: {{ formatPrice(originalProfit) }}
                    </h3>
                    <h3>
                        {{ $t('label.profit') }}: {{ formatPrice(totalProfit) }}
                    </h3>
                    <line-chart :chartData="profitChartData" cssClasses="stats-line-chart"></line-chart>
                </div>
                <div class="col-lg-5 col-md-12">
                    <h3>
                        {{ $t('label.appointments') }}: {{ totalAppointments }}
                    </h3>
                    <line-chart :chartData="appointmentsChartData" cssClasses="stats-line-chart"></line-chart>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
  import InputWidget from '@/components/widgets/InputWidget'
  import Datepicker from '@/components/widgets/datepicker/VueDatepickerLocal'
  import _ from 'lodash'
  import Doughnut from '@/components/widgets/charts/DoughnutChart'
  import LineChart from '@/components/widgets/charts/LineChart'
  import settings from '@/services/settings'
  import Moment from 'moment'
  import {extendMoment} from 'moment-range'
  import UserImage from '@/components/widgets/UserImageWidget'
  import auth from '@/auth'

  const moment = extendMoment(Moment)

  export default {
    components: {UserImage, InputWidget, Datepicker, Doughnut, LineChart},
    name: 'OrganizationStatsPage',
    props: ['employees', 'statistics', 'from', 'to', 'visible'],
    data () {
      return {
        filter: 'profit'
      }
    },
    computed: {
      doughnutChartData () {
        let data = {
          'labels': [],
          'datasets': [
            {
              data: [],
              backgroundColor: settings.colors
            }
          ]
        }
        if (this.visible) {
          _.forEach(this.statistics.organization.services, service => {
            if (this.filter === 'profit') {
              data.labels.push(service.name)
              data.datasets[0].data.push(service.profit)
            } else {
              data.labels.push(service.name)
              data.datasets[0].data.push(service.customers_count)
            }
          })
          return data
        }
      },
      appointmentsChartData () {
        let data = {
          'labels': [],
          'datasets': [
            {
              label: 'Appointments',
              data: [],
              backgroundColor: 'rgba(5, 203, 225, .5)'
            }
          ]
        }
        let appointments = _.sortBy(_.unionBy(this.statistics.organization.appointments_count, this.getRange(), 'date'), 'date')
        _.forEach(appointments, appointment => {
          data.labels.push(appointment.date)
          data.datasets[0].data.push(appointment.count)
        })
        return data
      },
      profitChartData () {
        let data = {
          'labels': [],
          'datasets': [
            {
              label: 'Profit',
              data: [],
              backgroundColor: 'rgba(5, 203, 225, .5)'
            }
          ]
        }
        let appointments = _.sortBy(_.unionBy(this.statistics.organization.appointments_count, this.getRange(), 'date'), 'date')
        _.forEach(appointments, appointment => {
          data.labels.push(appointment.date)
          let profit = appointment.price - appointment.discount
          data.datasets[0].data.push(!isNaN(profit) ? profit : 0)
        })
        return data
      },
      range: {
        get () {
          return [
            this.from,
            this.to
          ]
        },
        set (value) {
          this.$emit('rangeChanged', value)
        }
      },
      totalProfit () {
        let total = 0
        _.forEach(this.statistics.organization.appointments_count, appointment => {
          total += (appointment.price - appointment.discount)
        })
        return total
      },
      originalProfit () {
        let total = 0
        _.forEach(this.statistics.organization.appointments_count, appointment => {
          total += appointment.price
        })
        return total
      },
      totalAppointments () {
        let total = 0
        _.forEach(this.statistics.organization.appointments_count, appointment => {
          total += appointment.count
        })
        return total
      },
      employeeStats () {
        return _.orderBy(this.statistics.employees, 'totalProfit', 'desc')
      }
    },
    watch: {

    },
    methods: {
      profit (employee) {
        let amount = 0
        _.forEach(employee.services, service => {
          amount += service.profit
        })
        employee.totalProfit = amount
        return this.formatPrice(amount)
      },
      customers (employee) {
        let count = 0
        _.forEach(employee.services, service => {
          count += service.customers_count
        })
        employee.totalCustomers = count
        return count
      },
      getRange () {
        let data = []
        let range = moment.range(this.$moment(this.from), this.$moment(this.to))
        let i = 0
        for (let day of range.by('day')) {
          data[i] = {
            date: day.format('YYYY-MM-DD'),
            count: 0,
            price: 0
          }
          i++
        }
        return data
      },
      formatPrice (val) {
        return val + ' ' + auth.getOrganization().currency.toUpperCase()
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/pages/Organization/Stats/OrganizationStats.vue